/* eslint-disable no-template-curly-in-string */

import React, { useEffect, useMemo, useRef, useState } from "react";
import { reduxForm, Field, getFormValues } from "redux-form";
import { useDispatch, useSelector } from "react-redux";
import {
	ReduxControls,
	Blade,
	Button,
	ProgressIndicator,
	ErrorBanner
} from "rhombus-react";

import { CONTENT_FEED_DECISIONING_FORM } from "widgets/forms/list";
import { confirmOverwriteContentFeedDecisioning } from "reducers/Confirm";
import { loadCreatives } from "reducers/Creatives";
import { createContentFeedDecisioning, getContentFeedDecisioning, resetContentFeedDecisioningModalState } from "reducers/ContentFeedDecisioning";
import { loadContentFeedDetails } from "reducers/ContentFeedDetails";
import { loadContentFeedMapping } from "reducers/ContentFeedMapping";
import RotationModePanel from "./RotationModePanel";
import { noop } from "utils/common";

import { useFlag } from "@innovid/feature-flags-react";
import { FEATURE_FLAGS } from "featureFlag/featureFlag";

import "./ContentFeedDecisioningModal.css";

const DISALLOWED_COLUMNS = ["versionId", "versionName"];

let ContentFeedDecisioningForm = ({
									  handleSubmit,
									  onSubmit,
									  versionNameOptions,
									  reportingLabelOptions,
									  weightOptions,
									  showWeightColumn,
								  }) => {
	return (
		<form id={CONTENT_FEED_DECISIONING_FORM} onSubmit={handleSubmit(onSubmit)}>
			<Field
				name="options.decisioningName"
				component={ReduxControls.Input}
				label="Decisioning Name"
				placeholder="Enter name"
				required
			/>
			<Field
				name="filter.value"
				component={ReduxControls.Input}
				label="Filter Formula"
				placeholder={'Enter formula. Example: ${filter}="name_here"'}
			/>
			<Field
				name="targeting.formula"
				className="targeting-formula-field"
				component={ReduxControls.Input}
				label="Targeting Formula"
				placeholder={"geo(country=\"${country_column}\") & language(value=\"${lang_column}\")"}
				required
				type="textarea"
			/>
			<Field
				name="options.defaultVersion.name"
				label="Default Version"
				placeholder="Select Default Version"
				options={versionNameOptions}
				isClearable={false}
				onMenuScrollToBottom={noop}
				required
				component={ReduxControls.Select}
				normalize={value => value?.value}
				format={value => ({ label: value, value })}
			/>
			<Field
				name="options.reportingLabelColumn"
				label="Reporting Label Column"
				placeholder="Select Reporting Label Column"
				options={reportingLabelOptions}
				isClearable={false}
				onMenuScrollToBottom={noop}
				required
				component={ReduxControls.Select}
				normalize={value => value?.value}
				format={value => ({ label: value, value })}
			/>
			{showWeightColumn && (
				<Field
					name="options.weightColumn"
					label="Weight Column"
					placeholder="Select Weight Column"
					isClearable={false}
					component={ReduxControls.Select}
					onMenuScrollToBottom={noop}
					options={weightOptions}
					normalize={value => value?.value}
					format={value => ({ label: value, value })}
				/>
			)}
		</form>
	);
};

export default function ContentFeedDecisioningModal(props) {
	const {
		libraryId,
		creativeId,
		creativeName,
		contentFeedUrl,
		onClose = noop,
	} = props;
	const dispatch = useDispatch();
	const isAutoOptEnabled = useFlag(FEATURE_FLAGS.METANODE_AUTO_OPT);
	const isLoading = useSelector(state => state.contentFeedDecisioning.isLoading);
	const loadedValues = useSelector(state => state.contentFeedDecisioning.data);

	const feedDetailsData = useSelector(state => state.contentFeedDetails.data);
	const isLoadingFeedDetails = useSelector(state => state.contentFeedDetails.isLoading);

	const contentFeedMapping = useSelector(state => state.contentFeedMapping.data);
	const isLoadingFeedMapping = useSelector(state => state.contentFeedMapping.isLoading);

	const errors = useSelector(state => state.contentFeedDecisioning.errors);
	const errorDescription = useSelector(state => state.contentFeedDecisioning.errorDescription);

	const formValues = useSelector(state => getFormValues(CONTENT_FEED_DECISIONING_FORM)(state));
	const { weightColumn: weightColumnValue, reportingLabelColumn: reportingLabelColumnValue } = formValues?.options || {};

	const contentFeedId = contentFeedMapping?.[0]?.idContentFeed;
	const isEdit = !!Object.keys(loadedValues).length;

	// Holds the latest rotation options without causing re-renders on every field change.
	const rotationOptionsRef = useRef({ weightColumn: undefined, autoOptimization: undefined });
	const [isRotationValid, setIsRotationValid] = useState(true);

	const versionNameOptions = useMemo(() => {
		return feedDetailsData.reduce((acc, curr) => {
			const { versionName } = curr;
			if (!acc.some(v => v.value === versionName)) {
				acc.push({ label: versionName, value: versionName });
			}
			return acc;
		}, []);
	}, [feedDetailsData]);

	const {
		initialFeedDetailsKeys,
		filteredFeedDetailsKeys,
	} = useMemo(() => {
		const feedDetailsKeys = Object.keys(feedDetailsData?.[0]?.data || {});
		return feedDetailsKeys.reduce((acc, curr) => {
			if (DISALLOWED_COLUMNS.includes(curr)) {
				return acc;
			}

			const option = { label: curr, value: curr };
			acc.initialFeedDetailsKeys.push(option);

			if (curr !== weightColumnValue && curr !== reportingLabelColumnValue) {
				acc.filteredFeedDetailsKeys.push(option);
			}

			return acc;
		}, {
			initialFeedDetailsKeys: [],
			filteredFeedDetailsKeys: [],
		});
	}, [feedDetailsData, weightColumnValue, reportingLabelColumnValue]);

	const initialValues = useMemo(() => {
		if (isEdit) {
			return loadedValues;
		}

		if (feedDetailsData.length) {
			return {
				options: {
					defaultVersion: { name: versionNameOptions?.[0]?.value },
					reportingLabelColumn: initialFeedDetailsKeys?.[0]?.value,
				},
			};
		}
	}, [loadedValues, feedDetailsData, versionNameOptions, initialFeedDetailsKeys, isEdit]);

	const handleSuccess = () => {
		dispatch(loadCreatives(libraryId));
		handleClose();
	};

	const onSubmit = (values) => {
		const payload = { ...values };

		if (isAutoOptEnabled) {
			payload.options = {
				...payload.options,
				...rotationOptionsRef.current,
			};
		}

		if (isEdit) {
			return dispatch(confirmOverwriteContentFeedDecisioning({
				creativeId,
				values: payload,
				onSuccess: handleSuccess,
			}));
		}

		return dispatch(createContentFeedDecisioning(creativeId, payload)).then((resp) => {
			if (resp.success) {
				handleSuccess();
			}
		});
	};

	const handleClose = () => {
		dispatch(resetContentFeedDecisioningModalState());
		onClose();
	};

	useEffect(() => {
		if (contentFeedUrl) {
			dispatch(getContentFeedDecisioning(creativeId));
			dispatch(loadContentFeedMapping([creativeId]));
		}
	}, [contentFeedUrl, creativeId]);

	useEffect(() => {
		if (contentFeedId) {
			dispatch(loadContentFeedDetails(contentFeedId, { data_mode: true }));
		}
	}, [contentFeedId]);

	const renderContent = () => {
		return (
			<>
				<ProgressIndicator isLoading={isLoading || isLoadingFeedDetails || isLoadingFeedMapping} />
				{(errors.length || errorDescription) && <ErrorBanner messages={errors} title={errorDescription} />}
				<ContentFeedDecisioningForm
					onSubmit={onSubmit}
					initialValues={initialValues}
					versionNameOptions={versionNameOptions}
					reportingLabelOptions={filteredFeedDetailsKeys}
					weightOptions={filteredFeedDetailsKeys}
					showWeightColumn={!isAutoOptEnabled}
				/>
				{isAutoOptEnabled && (
					<RotationModePanel
						loadedValues={loadedValues}
						columnOptions={filteredFeedDetailsKeys}
						onChange={({ isValid, ...options }) => {
							rotationOptionsRef.current = options;
							setIsRotationValid(isValid);
						}}
					/>
				)}
			</>
		);
	};

	const renderFooter = () => {
		return (
			<>
				<Button theme="default" onClick={handleClose}>Cancel</Button>
				<Button
					type="submit"
					theme="green"
					disabled={isLoading || isLoadingFeedDetails || (isAutoOptEnabled && !isRotationValid)}
					form={CONTENT_FEED_DECISIONING_FORM}
				>Save
				</Button>
			</>
		);
	};

	return (
		<Blade
			type="blade"
			title={`Assign Bulk Sheet Decisioning - ${creativeName}`}
			className="content-feed-decisioning-modal"
			allowFullScreen={false}
			onClose={handleClose}
			renderContent={renderContent}
			renderFooter={renderFooter}
		/>
	);
}

ContentFeedDecisioningForm = reduxForm({
	form: CONTENT_FEED_DECISIONING_FORM,
	enableReinitialize: true,
})(ContentFeedDecisioningForm);
